import { useLayoutEffect } from "react";
import ErrMessage from "../components/core/ErrMessage.tsx";
import { Link, Outlet } from "react-router-dom";
import {
  IconBell,
  IconEdit,
  IconInfoHexagon,
  IconList,
  IconLogout,
  IconSettings,
  IconTerminal,
  IconUser,
  IconUserScreen,
  IconUserX,
} from "@tabler/icons-react";
import PopUpDialog from "../components/core/PopUpDialog";
import {
  ROUTE_ADMIN,
  ROUTE_EDIT_PROFILE,
  ROUTE_SETTING,
} from "../const/routes";
import Authorized from "../components/core/auth/Authorized";
import Footer from "../components/Footer";
import { useCurrentUser } from "../hooks/useCurrentUser";
import PopUp, { openModal } from "../components/core/PopUp";
import ReportPost from "../components/report/ReportPost";
import { SubmitProvider } from "../context/context.submit";
import ItemLayoutContainer from "../components/item/page/ItemLayoutContainer";
import { useAuthToken } from "../hooks/useAuthToken";
import LoadingPage from "../components/core/loading/LoadingPage";
import AccountDelete from "../components/login/AccountDelete.tsx";
import { useSubscribeChat } from "../hooks/useSubscribeChat.ts";
import { UserRole } from "../types/User.ts";
import ThemeToggle from "../components/core/theme/ThemeToggle.tsx";
import CompactToggle from "../components/core/CompactToggle.tsx";
import PageButton from "../components/core/PageSelection/PageButton.tsx";
import { useSearchParamsHandler } from "../hooks/useSearchParamsHandler.ts";
import { ITEM_STATUS } from "../types/Item.ts";
import SEO from "../components/core/SEO.tsx";
import ItemAmount from "../components/item/core/ItemAmount.tsx";
import { MAX_ITEM_AMOUNT } from "../const/enviroment.ts";
import UserCompact from "../components/user/view/UserCompact.tsx";

function SettingPage() {
  const currentUser = useCurrentUser();
  const authToken = useAuthToken();
  const subscribe = useSubscribeChat();
  const { set, get } = useSearchParamsHandler("status", "all");
  useLayoutEffect(() => scrollTo({ top: 0, behavior: "instant" }), []);

  if (authToken.status === "loading") return <LoadingPage />;

  if (authToken.status === "error")
    return <ErrMessage message={authToken.error} />;

  return (
    <div className="flex flex-col items-center safe-top-with-hanger safe-bottom-with-dock">
      <SEO url={ROUTE_SETTING} />
      <Outlet />
      <PopUp
        id={"user-deletion"}
        title={
          <>
            <IconUserX />
            Account löschen?
          </>
        }
      >
        <SubmitProvider iButtonElem={<>Profillöschung beantragen</>}>
          <AccountDelete />
        </SubmitProvider>
      </PopUp>
      <PopUp
        id={"user-data"}
        title={
          <>
            <IconUserScreen />
            Daten Anfragen?
          </>
        }
      >
        <SubmitProvider iButtonElem={<>Profildaten beantragen</>}>
          <ReportPost
            userId={currentUser}
            context="Datenanfrage stellen. **Account-Email unbedingt hier angeben** und eventuell Email and die gesendet werden soll angeben, da sonst kein Kommunikationsweg existiert!"
          />
        </SubmitProvider>
      </PopUp>
      <main className="flex flex-col gap-5 w-full items-center p-page min-h-screen">
        <section className="w-full max-w-lg">
          <ul className="list gap-5">
            <li className="p-4 tracking-wide">
              <div className="flex gap-2 items-center text-xl">
                <IconUser />
                Profil
              </div>
            </li>

            <li>
              <UserCompact userId={currentUser}></UserCompact>
            </li>

            <li>
              <Link
                className="btn w-full list-col-grow"
                to={ROUTE_EDIT_PROFILE}
              >
                <IconEdit />
                Profil bearbeiten
              </Link>
            </li>

            <li>
              <PopUpDialog
                id={`dialog-logout`}
                onClick={authToken.logout}
                title={
                  <>
                    <IconLogout />
                    Ausloggen?
                  </>
                }
                description="Wenn du dich auslogst kannst du dich später mit deiner E-Mail und Passwort erneut anmelden."
                className="btn w-full text-error active:text-error-content active:btn-error"
              >
                <IconLogout />
                Ausloggen
              </PopUpDialog>
            </li>
          </ul>
          <div className="divider"></div>

          <ul className="list gap-5">
            <li className="p-4 tracking-wide">
              <div className="flex gap-2 items-center text-xl">
                <IconSettings />
                Einstellungen
              </div>
            </li>
            <Authorized role={UserRole.Admin}>
              <li>
                <Link className="btn w-full" to={ROUTE_ADMIN}>
                  <IconTerminal />
                  Öffene Admin-Panel
                </Link>
              </li>
            </Authorized>
            {subscribe.isSubscribed ===
            "unsupported" ? null : subscribe.isSubscribed ? (
              <li>
                <PopUpDialog
                  id={`dialog-notification-disable`}
                  loading={false}
                  onClick={subscribe.unsubscribeChat}
                  title={
                    <>
                      <IconBell />
                      Deaktivieren?
                    </>
                  }
                  description="Deaktiviere Benachrichtigungen. Dies ist aktuell noch in der Testphase und benachrichtigt nur bei neu eingehenden Chatnachrichten."
                  className="btn w-full"
                >
                  <IconBell />
                  Deaktiviere Benachrichtigungen (Beta)
                </PopUpDialog>
              </li>
            ) : (
              <li>
                <PopUpDialog
                  id={`dialog-notification-enable`}
                  loading={false}
                  onClick={subscribe.subscribeChat}
                  title={
                    <>
                      <IconBell />
                      Aktivieren?
                    </>
                  }
                  description="Aktiviere Benachrichtigungen. Dies ist aktuell noch in der Testphase und benachrichtigt nur bei neu eingehenden Chatnachrichten."
                  className="btn w-full"
                >
                  <IconBell />
                  Aktiviere Benachrichtigungen (Beta)
                </PopUpDialog>
              </li>
            )}

            <li>
              <ThemeToggle
                className="btn w-full"
                textLight="zu Dark wechseln"
                textDark="zu Hell wechseln"
              />
            </li>
            <li>
              <CompactToggle
                listText="Kompakt"
                cardText="Locker"
                className="btn w-full"
              />
            </li>
            <li>
              <Link className="btn w-full list-col-grow" to="./reports">
                <IconUser />
                Reports einsehen
              </Link>
            </li>
          </ul>
          <div className="divider"></div>

          <ul className="list gap-5">
            <li className="p-4 tracking-wide">
              <div className="flex gap-2 items-center text-xl">
                <IconInfoHexagon />
                Datenauskunft und Löschung
              </div>
            </li>

            <li>
              <PopUpDialog
                title={
                  <>
                    <IconUserScreen />
                    Gespeicherte Daten anfordern?
                  </>
                }
                id={`dialog-profile-data`}
                loading={false}
                onClick={() => openModal("user-data")}
                description={
                  "Du kannst eine Kopie aller personenbezogenen Daten anfordern, die wir zu deinem Konto gespeichert haben. Der Betreiber prüft den Antrag und stellt die Daten innerhalb der gesetzlich vorgeschriebenen Frist bereit."
                }
                className="btn w-full text-warning"
              >
                <IconUserScreen />
                Gespeicherte Daten anfordern
              </PopUpDialog>
            </li>
            <li>
              <PopUpDialog
                title={
                  <>
                    <IconUserX />
                    Account löschen?
                  </>
                }
                id={`dialog-profile-delete`}
                onClick={() => openModal("user-deletion")}
                className="btn w-full text-error"
                description={
                  "Deine Daten werden gemäß DSGVO nach den gesetzlich vorgeschriebenen Schritten gelöscht. Alle personenbezogenen Daten des Kontos, werden spätestens innerhalb der nächsten 30 Tage gelöscht."
                }
              >
                <IconUserX />
                Account löschen
              </PopUpDialog>
            </li>
          </ul>
        </section>
        <div className="divider"></div>
        <section className="flex flex-col w-full gap-5">
          <h5 className="flex gap-2 items-center text-xl">
            <IconList />
            Deine Inserate: <ItemAmount userId={currentUser} /> / {""}
            {MAX_ITEM_AMOUNT} gleichzeitig
          </h5>

          <div className="flex w-full gap-2 text-xl justify-end items-center">
            Anzeigen:
            <select
              defaultValue={get()[0] ?? "all"}
              className="select"
              onChange={(e) => {
                set(e.target.value);
              }}
            >
              <option aria-label="Alle" value="all">
                Alle
              </option>
              <option aria-label="Aktive" value={ITEM_STATUS.ACTIVE}>
                Aktive
              </option>
              <option aria-label="Reserviert" value={ITEM_STATUS.RESERVED}>
                Reserviert
              </option>
              <option aria-label="Deaktiviert" value={ITEM_STATUS.HIDDEN}>
                Deaktiviert
              </option>
            </select>
          </div>

          <div className="w-full">
            <SubmitProvider>
              <ItemLayoutContainer userId={currentUser} />
            </SubmitProvider>
          </div>
          <div className="w-full flex justify-center justify-self-end p-4">
            <PageButton />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default SettingPage;
